import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import CenteredSpinner from 'app/components/CenteredSpinner';
import {useNavSidebarContext} from 'app/components/NavSidebar/NavSidebarProvider';
import {usePushNotification} from 'app/components/Notification';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganizationAreaUnit, getAreaUnit} from 'app/modules/Remote/Organization';
import {useUserInfo} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {NOTE_TYPE_AGENT, NOTE_TYPE_USER} from 'app/utils/constants';
import {calculateAreaInUnit} from 'app/utils/geoJsonUtils';
import * as historyUtils from 'app/utils/historyUtils';
import {formatDate} from 'app/utils/imageryUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {DataConfigDisplay} from './DataConfigDisplay';
import {FeatureResultsTable} from './FeatureResultsTable';
import {useAgentRunQuery, useBatchUpdateNotes} from './hooks';
import {NoteFilterBar} from './NoteFilterBar';
import {ReviewObservationsWrapper} from './ReviewObservations';
import cs from './styles.module.scss';
import {
  AgentFeatureResult,
  ApiAgentChildRun,
  ApiAgentNote,
  ApiAgentRun,
  ApiAgentWorkflow,
  NoteQueueFilterState,
} from './types';
import {buildFeatureResults, sortAndFilterNotes} from './utils';

type Props = {
  runId: string;
  workflow: ApiAgentWorkflow;
};

type RunViewTab = 'summary' | 'observations' | 'metadata';

// Stable default for runs predating per-property child runs, so the summary memos still hold.
const NO_CHILD_RUNS: ApiAgentChildRun[] = [];

export const RunView = ({runId, workflow}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [organization] = useUserInfo();
  const areaUnit = getAreaUnit(organization);
  const pushNotification = usePushNotification();

  // TODO: workflow.id is a number, but we have it typed  as a string.
  // On the note, both run_id and workflow_id are numbers.
  const {data, isLoading} = useAgentRunQuery(String(workflow.id), String(runId));
  const [selectedView, setSelectedView] = React.useState<RunViewTab>('summary');
  const notes = (data as ApiAgentRun)?.notes;
  const features = (data as ApiAgentRun)?.features;
  const agentRuns = (data as ApiAgentRun)?.agentRuns ?? NO_CHILD_RUNS;
  const runDataConfig = (data as ApiAgentRun)?.dataConfig;

  const csvExportFilename = `Lens_Agent_Results_${workflow.title}_${formatDate(data?.startedAt ?? new Date())}`;

  const [filters, setFilters] = React.useState<NoteQueueFilterState>({
    status: 'all',
    sortParams: ['featureName', 'asc'],
    searchParams: ['featureName', ''],
  });

  const setFilterState = React.useCallback(
    (partial: Partial<NoteQueueFilterState>) => setFilters((prev) => ({...prev, ...partial})),
    []
  );

  // noteQueue are the the notes that we manipulate in state with sorts and filters
  const [noteQueue, setNoteQueue] = React.useState<ApiAgentNote[]>(notes ?? []);
  const [focusedNoteId, setFocusedNoteId] = React.useState<string | null>(null);

  // anytime filters/sorts change, re-sort the noteQueue to get the latest queue order
  React.useEffect(() => {
    if (notes && features) {
      const filteredAndSortedNotes = sortAndFilterNotes(notes, features, filters, areaUnit);
      setNoteQueue(filteredAndSortedNotes);
    }
    // notes purposely not included in dependencies!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, features, areaUnit]);

  // HOWEVER, if an individual note changes (eg is saved/dismissed), we want to update
  // the note in its current position in the queue without changing the order, and only
  // change the queue order if sorts/filters change
  React.useEffect(() => {
    if (notes && noteQueue.length > 0) {
      setNoteQueue((currentQueue) => {
        return currentQueue.map((queueNote) => {
          const updatedNote = notes.find((note) => note.id === queueNote.id);
          return updatedNote ?? queueNote;
        });
      });
    }
  }, [notes, noteQueue.length]);

  // hide the sidebar once we in this view, we need the real estate
  const {setCollapsedView} = useNavSidebarContext();
  React.useEffect(() => {
    setCollapsedView(true);
    // only do this on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navToFocusedNote = (noteId: string | number) => {
    if (selectedView !== 'observations') setSelectedView('observations');
    setFocusedNoteId(String(noteId));
    historyUtils.updateHash(
      navigate,
      location,
      routeUtils.NOTE_URL_PARAM_REGEX,
      `${routeUtils.NOTE_URL_PARAM}${String(noteId)}`
    );
  };

  // Bulk-action selection, keyed by note id so it survives re-sorts and re-filters of the queue.
  const [selectedNoteIds, setSelectedNoteIds] = React.useState<ReadonlySet<string>>(new Set());
  const selectedNotes = React.useMemo(
    () => (notes ?? []).filter((note) => selectedNoteIds.has(String(note.id))),
    [notes, selectedNoteIds]
  );
  const toggleNoteSelected = React.useCallback((noteId: string | number) => {
    setSelectedNoteIds((prev) => {
      const next = new Set(prev);
      const id = String(noteId);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const clearSelection = React.useCallback(() => setSelectedNoteIds(new Set()), []);
  const selectAllNotes = React.useCallback(
    () => setSelectedNoteIds(new Set(noteQueue.map((note) => String(note.id)))),
    [noteQueue]
  );

  // Load into note via url. Runs once when the queue first has content: the hash lags a beat
  // behind focusNote (router navigations are transitions), so re-reading it after a note
  // mutation would snap focus back to the note the user just left.
  const loadedNoteFromUrl = React.useRef(false);
  React.useEffect(() => {
    if (loadedNoteFromUrl.current || !noteQueue || noteQueue.length === 0) return;
    loadedNoteFromUrl.current = true;
    const noteIdMatch = historyUtils.matchInHash(location, routeUtils.NOTE_URL_PARAM_REGEX);
    if (!noteIdMatch || noteIdMatch.length <= 2) {
      // If note hash is not present
      return;
    }
    const [, , noteIdStr] = noteIdMatch;
    const hasNote = noteQueue.find((n) => String(n.id) === noteIdStr);
    if (!hasNote) {
      // Notify the user and strip the URL param on invalid note
      pushNotification({
        message: 'The note linked in the URL was not found',
        autoHideDuration: 3000,
        options: {intent: B.Intent.WARNING, icon: 'info-sign'},
      });
      historyUtils.updateHash(navigate, location, routeUtils.NOTE_URL_PARAM_REGEX, '');
      return;
    }
    setSelectedView('observations');
    setFocusedNoteId(noteIdStr);
    // Only want to run this effect after page load, so we don't need to depend on history
    // or location
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteQueue, pushNotification, setSelectedView]);

  // Bulk actions
  const batchNoteMutation = useBatchUpdateNotes(workflow.id, runId);
  const handleBatchSave = React.useCallback(() => {
    batchNoteMutation.mutate(
      selectedNotes.map((note) => ({
        ...note,
        noteType: NOTE_TYPE_USER as ApiAgentNote['noteType'],
        isArchived: false,
      }))
    );
  }, [selectedNotes, batchNoteMutation]);

  const handleBatchDismiss = React.useCallback(() => {
    batchNoteMutation.mutate(
      selectedNotes.map((note) => ({
        ...note,
        noteType: NOTE_TYPE_AGENT as ApiAgentNote['noteType'],
        isArchived: true,
      }))
    );
  }, [selectedNotes, batchNoteMutation]);

  if (isLoading) {
    return (
      <div className={cs.page}>
        <CenteredSpinner />
      </div>
    );
  }

  if (!data) {
    return <div>Run not found.</div>;
  }

  return (
    <div className={classNames(cs.page, cs.pageTightSpace)}>
      <div className={cs.runViewHeader}>
        <B.Breadcrumbs
          items={[
            {
              text: 'Run plans',
              onClick: () => {
                navigate(`/${organization.get('id')}/agent`);
              },
            },
            {
              text: workflow.title,
              onClick: () => {
                navigate(`/${organization.get('id')}/agent/workflows/${workflow.id}`);
              },
            },
            {
              text: 'Run ' + formatDate(data.startedAt),
              onClick: () => {
                navigate(`/${organization.get('id')}/agent/workflows/${workflow.id}/runs/${runId}`);
              },
            },
          ]}
        />
        <B.SegmentedControl
          className={cs.runViewTabs}
          intent="primary"
          options={[
            {label: 'Summary', value: 'summary'},
            {label: `Observations (${notes?.length ?? 0})`, value: 'observations'},
            {label: 'Metadata', value: 'metadata'},
          ]}
          value={selectedView}
          onValueChange={(value) => {
            setSelectedView(value as RunViewTab);
            if (value !== 'observations') {
              setFocusedNoteId(null);
              historyUtils.updateHash(navigate, location, routeUtils.NOTE_URL_PARAM_REGEX, '');
            } else {
              historyUtils.updateHash(
                navigate,
                location,
                routeUtils.NOTE_URL_PARAM_REGEX,
                `${routeUtils.NOTE_URL_PARAM}${String(noteQueue?.[0]?.id)}`
              );
            }
            clearSelection();
          }}
        />
      </div>

      {selectedView === 'observations' && (
        <NoteFilterBar
          filters={filters}
          setFilterState={setFilterState}
          noteQueue={noteQueue}
          notes={notes}
          features={features}
          areaUnit={areaUnit}
          csvExportFilename={csvExportFilename}
          selectedNotes={selectedNotes}
          clearSelection={clearSelection}
          selectAllNotes={selectAllNotes}
          bulkSaveNotes={handleBatchSave}
          bulkDismissNotes={handleBatchDismiss}
        />
      )}
      {selectedView === 'summary' && (
        <RunSummaryPanel
          agentRuns={agentRuns}
          notes={notes}
          features={features}
          areaUnit={areaUnit}
          organizationId={organization.get('id')}
        />
      )}
      {selectedView === 'observations' &&
        (noteQueue?.length > 0 ? (
          <ReviewObservationsWrapper
            noteQueue={noteQueue}
            features={(data as ApiAgentRun).features}
            runId={runId}
            focusedNoteId={focusedNoteId}
            focusNote={navToFocusedNote}
            areaUnit={areaUnit}
            selectedNoteIds={selectedNoteIds}
            toggleNoteSelected={toggleNoteSelected}
          />
        ) : (
          <div>No observations found</div>
        ))}
      {selectedView === 'metadata' && (
        <DataConfigDisplay dataConfig={runDataConfig} runId={runId} />
      )}
    </div>
  );
};

// The Summary tab: the run's roll-up plus every property it touched, including the ones that
// produced no observations and so never appear in the table or on the map.
const RunSummaryPanel: React.FC<{
  agentRuns: ApiAgentChildRun[];
  notes: ApiAgentNote[];
  features: ApiFeature[];
  areaUnit: ApiOrganizationAreaUnit;
  organizationId: string;
}> = ({agentRuns, notes, features, areaUnit, organizationId}) => {
  const featureResults = React.useMemo(
    () => buildFeatureResults(agentRuns, notes, features, areaUnit),
    [agentRuns, notes, features, areaUnit]
  );

  // The run's features don't carry their project, so it's recovered through the feature
  // collection each project lists.
  const [projects] = useProjects();
  const projectIdByFeatureCollectionId = React.useMemo(() => {
    const map = new Map<number, string>();
    projects?.forEach((project) => {
      project!.get('featureCollections').forEach((pfc) => {
        map.set(pfc!.get('id'), project!.get('id'));
      });
    });
    return map;
  }, [projects]);

  const featuresById = React.useMemo(
    () => new Map(features.map((feature) => [Number(feature.id), feature])),
    [features]
  );

  const getPropertyHref = React.useCallback(
    (result: AgentFeatureResult) => {
      const feature = featuresById.get(Number(result.featureId));
      const projectId =
        feature && projectIdByFeatureCollectionId.get(feature.properties.featureCollectionId);
      if (!projectId) return null;
      return routeUtils.makeProjectDashboardUrl(
        organizationId,
        projectId,
        'map',
        [result.featureId],
        undefined,
        {includeRoot: true}
      );
    },
    [featuresById, projectIdByFeatureCollectionId, organizationId]
  );

  return (
    <div className={cs.runSummaryPanel}>
      <div className={cs.runSummaryHeader}>
        <h4 className={cs.runSummaryTitle}>Results by property ({featureResults.length})</h4>
        <RunSummary features={features} notes={notes} areaUnit={areaUnit} />
      </div>
      {featureResults.length > 0 ? (
        <FeatureResultsTable
          results={featureResults}
          areaUnit={areaUnit}
          getPropertyHref={getPropertyHref}
        />
      ) : (
        <div className={cs.runSummaryEmpty}>
          This run doesn't have per-property results. Findings are in the Observations view.
        </div>
      )}
    </div>
  );
};

export const RunSummary: React.FC<{
  features: ApiFeature[];
  notes: ApiAgentNote[];
  areaUnit: ApiOrganizationAreaUnit;
}> = ({features, notes, areaUnit}) => {
  // Area is a projection per polygon vertex, so it stays out of the render path.
  const totalArea = React.useMemo(
    () =>
      features.reduce(
        (acc, feature) => acc + (calculateAreaInUnit(feature.geometry, areaUnit) ?? 0),
        0
      ),
    [features, areaUnit]
  );

  const featuresWithFindings = React.useMemo(() => {
    const featureIdsWithNotes = new Set(notes.map((note) => note.featureId));
    return features.filter((feature) => featureIdsWithNotes.has(feature.id)).length;
  }, [features, notes]);

  const formattedNumber = (number: number) => {
    return number.toLocaleString('en-US', {maximumFractionDigits: 0});
  };
  return (
    <div>
      {formattedNumber(features.length)} properties reviewed across {formattedNumber(totalArea)}{' '}
      {areaUnit === 'areaAcre' ? 'acres' : 'hectares'} · {formattedNumber(notes.length)} findings on{' '}
      {formattedNumber(featuresWithFindings)} properties
    </div>
  );
};
